// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  FormField,
  Input,
  RadioGroup,
  Select,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react-v3/polaris';
import ExcludedArcsField from '../../Common/ExcludedArcsField';
import { RetryAlert } from '../../Common/ErrorMessages';
import { useLocales } from '../../../utils/locales';
import { TER_MULTIPLIER_FORMULA } from '../../../utils/terMultiplier';
import { BasePrice, BaseRates } from '../BaseRates/baseRates';
import { arcOptions } from '../PricingModels/pricingModels';
import {
  lacksDefaultTers,
  missingDefaultTersMessage,
  TER_GROUPS_PATH,
  terGroupPath,
  terGroupRoute,
  TerGroup,
  TerGroupSummary,
} from '../TerGroups/terGroups';
import ScopingModelPreview from './ScopingModelPreview';
import * as scopingModels from './scopingModels';
import { Loaded, LoadClient, useLoaded } from './useLoaded';

interface ScopingModelFieldsProps {
  apiClient: LoadClient;
  // Loaded by the page, whose submit button waits for it.
  baseRate: Loaded<BaseRates>;
  values: scopingModels.FormValues;
  fieldErrors: scopingModels.FieldErrors;
  disabled: boolean;
  // A create enters the name and description, which an edit shows but cannot change.
  creating: boolean;
  onChange: (field: scopingModels.FormField, value: string | string[]) => void;
}

const MODE_ITEMS = [
  { value: 'value', label: 'Fixed value', description: 'The same weight for each base-rate arc.' },
  {
    value: 'multiplier',
    label: 'TER multiplier',
    description: 'Weights each base-rate arc by its TER in a TER group.',
  },
];

const NO_BASE_PRICES: BasePrice[] = [];

/** The inputs of a scoping model, with a live preview of its weight per arc. */
const ScopingModelFields = ({
  apiClient,
  baseRate,
  values,
  fieldErrors,
  disabled,
  creating,
  onChange,
}: ScopingModelFieldsProps): JSX.Element => {
  const multiplier = scopingModels.usesMultiplier(values);
  const { locales, errors: localeErrors, reload: reloadLocales } = useLocales(
    apiClient,
    scopingModels.describeError
  );
  const groups = useLoaded<TerGroupSummary[]>(apiClient, TER_GROUPS_PATH);
  const group = useLoaded<TerGroup>(
    apiClient,
    multiplier && values.terGroupId ? terGroupPath(values.terGroupId) : null
  );

  const basePrices = baseRate.value ? baseRate.value.basePrices : NO_BASE_PRICES;
  const options = useMemo(() => arcOptions(basePrices, values.excludedArcs, locales), [
    basePrices,
    values.excludedArcs,
    locales,
  ]);
  const groupOptions = (groups.value || []).map(summary => ({
    value: summary.terGroup,
    label: summary.groupName,
    description: summary.workflow || undefined,
  }));
  const selectedGroup =
    groupOptions.find(option => option.value === values.terGroupId) ||
    (values.terGroupId === '' ? null : { value: values.terGroupId, label: values.terGroupId });
  const {
    zeroTo49Mode,
    zeroTo49Weight,
    zeroTo49TerMultiplier,
    fiftyTo74Mode,
    fiftyTo74Weight,
    fiftyTo74TerMultiplier,
    p75Mode,
    p75Weight,
    p75TerMultiplier,
    p85Mode,
    p85Weight,
    p85TerMultiplier,
    p95Mode,
    p95Weight,
    p95TerMultiplier,
    p100Mode,
    p100Weight,
    p100TerMultiplier,
    p101Mode,
    p101Weight,
    p101TerMultiplier,
    excludedArcs,
  } = values;
  // Built from the preview's own inputs only, so an edit elsewhere keeps its rows.
  const preview = useMemo(() => {
    const entries = {
      zeroTo49Mode,
      zeroTo49Weight,
      zeroTo49TerMultiplier,
      fiftyTo74Mode,
      fiftyTo74Weight,
      fiftyTo74TerMultiplier,
      p75Mode,
      p75Weight,
      p75TerMultiplier,
      p85Mode,
      p85Weight,
      p85TerMultiplier,
      p95Mode,
      p95Weight,
      p95TerMultiplier,
      p100Mode,
      p100Weight,
      p100TerMultiplier,
      p101Mode,
      p101Weight,
      p101TerMultiplier,
      excludedArcs,
    };
    if (
      !baseRate.value ||
      !scopingModels.entriesComplete(entries) ||
      (scopingModels.usesMultiplier(entries) && !group.value)
    ) {
      return null;
    }
    return scopingModels.previewOf(entries, basePrices, group.value);
  }, [
    baseRate.value,
    zeroTo49Mode,
    zeroTo49Weight,
    zeroTo49TerMultiplier,
    fiftyTo74Mode,
    fiftyTo74Weight,
    fiftyTo74TerMultiplier,
    p75Mode,
    p75Weight,
    p75TerMultiplier,
    p85Mode,
    p85Weight,
    p85TerMultiplier,
    p95Mode,
    p95Weight,
    p95TerMultiplier,
    p100Mode,
    p100Weight,
    p100TerMultiplier,
    p101Mode,
    p101Weight,
    p101TerMultiplier,
    excludedArcs,
    group.value,
    basePrices,
  ]);

  const weightInput = (
    field:
      | scopingModels.RepetitionField
      | scopingModels.WeightField
      | scopingModels.TerMultiplierField,
    label: string,
    description?: string
  ): JSX.Element => {
    // Create and Save wait for valid weights, so a typed error shows before either is clicked. A
    // text input keeps what was typed: a number input reports text it cannot parse as empty.
    const error = fieldErrors[field] || scopingModels.typedWeightError(values[field]);
    return (
      <FormField
        key={field}
        id={`${field}-field`}
        label={label}
        description={description}
        constraintText={scopingModels.WEIGHT_CONSTRAINT}
        errorText={error}
      >
        <Input
          id={field}
          inputMode="decimal"
          value={values[field]}
          invalid={Boolean(error)}
          disabled={disabled}
          onChange={({ detail }): void => onChange(field, detail.value)}
        />
      </FormField>
    );
  };

  return (
    <SpaceBetween size="m">
      {localeErrors.length > 0 && (
        <RetryAlert
          id="scopingModelLocalesError"
          header="Could not load the locales"
          messages={localeErrors}
          retryId="retryLocales"
          onRetry={reloadLocales}
        />
      )}
      <FormField
        id="name-field"
        label="Name"
        description={creating ? undefined : 'The name cannot be changed.'}
        constraintText={creating ? 'Required. The name cannot be changed later.' : undefined}
        errorText={fieldErrors.name}
      >
        <Input
          id="name"
          value={values.name}
          invalid={Boolean(fieldErrors.name)}
          disabled={disabled || !creating}
          onChange={({ detail }): void => onChange('name', detail.value)}
        />
      </FormField>
      <FormField
        id="description-field"
        label="Description"
        description={creating ? undefined : 'The description cannot be changed.'}
        constraintText={creating ? 'Required. The description cannot be changed later.' : undefined}
        errorText={fieldErrors.description}
      >
        <Textarea
          id="description"
          value={values.description}
          invalid={Boolean(fieldErrors.description)}
          disabled={disabled || !creating}
          onChange={({ detail }): void => onChange('description', detail.value)}
        />
      </FormField>
      {baseRate.missing && (
        <Alert id="scopingModelBaseRateMissing" type="warning">
          {`Base rates are required before ${creating ? 'creating' : 'saving'} a scoping model.`}{' '}
          <Link to="/settings/baseRate">Set up base rates</Link>
        </Alert>
      )}
      {baseRate.errors.length > 0 && (
        <RetryAlert
          id="scopingModelBaseRateError"
          header="Could not load the base rates"
          messages={baseRate.errors}
          retryId="retryBaseRate"
          onRetry={baseRate.reload}
        />
      )}
      {scopingModels.MATCH_BANDS.map(band => (
        <SpaceBetween key={band.band} size="s">
          <FormField id={`${band.mode}-field`} label={`${band.label} weight`}>
            <RadioGroup
              id={band.mode}
              items={MODE_ITEMS.map(item => ({ ...item, disabled }))}
              value={values[band.mode]}
              onChange={({ detail }): void => onChange(band.mode, detail.value)}
            />
          </FormField>
          {values[band.mode] === 'multiplier'
            ? weightInput(
                band.terMultiplier,
                `${band.label} ${scopingModels.TER_MULTIPLIER_LABEL}`,
                `${band.label} weight = ${TER_MULTIPLIER_FORMULA}. TER is the share of words a translator is expected to edit, paid in full. m is the share of the full rate paid for reviewing the remaining words. Example: TER 0.375 with m 0.35 gives 0.59.`
              )
            : weightInput(
                band.weight,
                band.label,
                'Used for each base-rate arc that is not excluded.'
              )}
        </SpaceBetween>
      ))}
      {weightInput(scopingModels.REPETITION.field, scopingModels.REPETITION.label)}
      {multiplier && (
        <>
          <FormField
            id="terGroupId-field"
            label="TER group"
            description="Each arc's TER comes from this group: its entry for the arc, else its bilingual or monolingual default TER."
            errorText={fieldErrors.terGroupId}
          >
            <Select
              id="terGroupId"
              options={groupOptions}
              selectedOption={selectedGroup}
              placeholder="Choose a TER group"
              filteringType="auto"
              statusType={groups.value || groups.errors.length > 0 ? 'finished' : 'loading'}
              loadingText="Loading TER groups"
              invalid={Boolean(fieldErrors.terGroupId)}
              disabled={disabled}
              onChange={({ detail }): void =>
                onChange('terGroupId', detail.selectedOption.value as string)
              }
            />
          </FormField>
          {groups.errors.length > 0 && (
            <RetryAlert
              id="scopingModelTerGroupsError"
              header="Could not load the TER groups"
              messages={groups.errors}
              retryId="retryTerGroups"
              onRetry={groups.reload}
            />
          )}
          {group.errors.length > 0 && (
            <RetryAlert
              id="scopingModelTerGroupError"
              header="Could not load the TER group for the preview"
              messages={group.errors}
              retryId="retryTerGroup"
              onRetry={group.reload}
            />
          )}
          {group.value && lacksDefaultTers(group.value) && (
            <Alert id="scopingModelTerGroupDefaultsMissing" type="warning">
              {missingDefaultTersMessage(group.value)}{' '}
              <Link to={terGroupRoute(group.value.terGroup)}>Open the TER group</Link>
            </Alert>
          )}
        </>
      )}
      <ExcludedArcsField
        options={options}
        selected={values.excludedArcs}
        description="Language pairs from the base rates that this scoping model does not weight."
        errorText={fieldErrors.excludedArcs}
        disabled={disabled}
        onChange={(selected): void => onChange('excludedArcs', selected)}
      />
      {preview && <ScopingModelPreview rows={preview} multiplier={multiplier} />}
    </SpaceBetween>
  );
};

export default ScopingModelFields;
